import uiConfig from 'src/lib/ui/config';
import { View } from '@play-co/timestep-core/ui';
import ResponsiveScene from './ResponsiveScene';
import StateObserver, { user } from 'src/StateObserver';
import * as Pet from 'src/replicant/pet';
import { Transition } from 'src/game/logic/SceneManager';
import {
  getFooter,
  getHeader,
  getSideButtonsY,
  waitForItPromise,
} from 'src/lib/utils';
import { isSceneEntered } from 'src/lib/stateUtils';
import statePromise from 'src/lib/statePromise';
import { getPet } from '../pet';
import LivingRoomDecorationView from '../decoration/LivingRoomDecorationView';
import { decorationTutorialFinishSequence } from '../decoration/decoration.sequence';
import * as Fate from '../../replicant/fate';
import { HomeButtonsAnimated } from '../components/HomeButtonsAnimated';
import { ChampionsLeagueButtonArena } from '../championsLeague/components/ChampionsLeagueButtonArena';
import { goToArenaFromHome } from '../championsLeague/championsLeague.sequence';
import sounds from 'src/lib/sounds';

export default class LivingRoomScene extends ResponsiveScene {
  private content: View;
  private decorationView: LivingRoomDecorationView;
  private buttons: HomeButtonsAnimated;
  private arenaButton: ChampionsLeagueButtonArena;

  public getIcon(icon: string): View | undefined {
    return this.buttons.getButton(icon);
  }

  public getHomeButtons() {
    return this.buttons;
  }

  constructor() {
    super(new View({ width: uiConfig.width, height: uiConfig.height }));

    this.content = new View({
      superview: this.getView(),
      width: uiConfig.width,
      height: uiConfig.height,
      infinite: true,
    });

    this.decorationView = this.content.addSubview(
      new LivingRoomDecorationView({
        autoDecorationUpdate: true,
      }),
    );

    this.getView().tag = 'livingRoom';

    this.buttons = new HomeButtonsAnimated({
      scene: 'livingRoom',
      superview: this.getView(),
      width: this.getView().style.width,
      height: 900,
      y: 180,
      canHandleEvents: false,
      zIndex: 9000,
    });

    this.arenaButton = new ChampionsLeagueButtonArena({
      superview: this.getView(),
      zIndex: 9000,
      onClick: async () => {
        sounds.playSound('ok');
        await goToArenaFromHome();
      },
    });

    this.onResize();
  }

  public async showScene() {
    // Start finish decoration tutorial
    await decorationTutorialFinishSequence();

    this.buttons.showScene();
    this.setSideButtonPositions();
    this.onResize();

    await statePromise(() => isSceneEntered('livingRoom'));
    await waitForItPromise(500).then(() => {
      const { user } = StateObserver.getState();
      const petExists = !!Pet.getters.getPetCreatedAt(user);
      const petInDaycare = Fate.getters.hasPermanentStatusEffect(
        user,
        'daycare',
        StateObserver.now(),
      );
      const appearanceLocked = getPet().appearance.isLocked();
      if (!petExists || petInDaycare || appearanceLocked) {
        return;
      }

      getPet().position.setWandering(true);
    });
  }

  public hideScene() {
    this.buttons.hideScene();
    getPet().position.setWandering(false);
  }

  private setSideButtonPositions() {
    const y = getSideButtonsY(this.getView());
    let height = this.getView().style.height - y;
    height -= getFooter()!.style.height;
    height -= 20; // Small marging

    this.buttons.updateOpts({ y, height });
  }

  public transition(): Transition {
    return 'slide_back';
  }

  public pauseScene() {
    // This will pause buttons updates
    this.buttons.hideScene();
  }

  public resumeScene(): void {
    // This will resume buttons updates
    this.buttons.showScene();
  }

  protected onResize = () => {
    this.arenaButton.updateOpts({
      x: 0,
      y: this.getView().style.height - getFooter()!.style.height - 200,
    });
  };
}
